import * as React from "react";
import { makeStyles, Text, Link, Image } from "@fluentui/react-components";
import { flexColumn, centered } from "../../styles/GlobalStyles";
import { CardSection } from "../../components/CardSection";
import { CodeBlock } from "../../components/CodeBlock";

const useStyles = makeStyles({
    flexColumn,
    centered,
    container: {
        display: "flex",
        flexDirection: "column",
        gap: "2rem",
        padding: "2rem clamp(1rem, 5vw, 4rem)",
        width: "80%",
        margin: "0 auto",
        maxWidth: "1400px",
    },
    ul: {
        paddingLeft: "1.25rem",
        marginTop: "0.5rem",
        marginBottom: "0.5rem",
    }
});

export const Id = "ef743385-3b40-429c-8ed6-0e463e314ffc";
export const Title = "How to use Ianus inside Canvas Apps?";
export const Subtitle = "Explanation on how to use Ianus inside Canvas Apps";
export const Description = "Find out how to use Ianus inside Canvas Apps and what to take care of";
export const Keywords = ["developer", "lowcode", "nocode", "canvas", "app"];

const codeView = `- IanusCanvas1:
    Control: CodeComponent
    ComponentName: ian_Ianua.IanusCanvas
    Properties:
      Items: =Licenses
      OnChange: =If(And(IanusCanvas1.isValid, IsBlank(Find("PowerApps-Studio", Host.Version))), Navigate(Screen1))
      productId: ="deadbeef-bb32-453e-83c9-f70d80610089"
      publicKey: |-
        ="-----BEGIN PUBLIC KEY-----
        MIIBIjANBgkqhkiG9w0BAQEFAAOCAQ8AMIIBCgKCAQEA6nVX0y33NJ8qHzEYGE8L
        JmUzHVfEeAQZS76ZSXAGjOkhAhu4Ow98OVdGLuH4v0QL7lUcsHROTm9yB7hqbNQU
        w7AVC3SYtDh9Wyr/oXgIluDruM5nJYuraWtKsDRAYkwS34dRlXx3y09wapG/e5sF
        CNEwyDWb6A+ObdoOJ7sX4co+3svihcpKHJ50mNovbYF9COMVOR4SvFVM/9m7oxJ2
        XQ29XiTbfVkOTSixAY0OREXydhZAEQ0MIlWU8xKhzh8eoPkpXeDWqNuSDHr4r8Pn
        b6ccO0Q2CBCqpyA2CXB8JSwEIdg5J59iY9mc6Wzw2vFZP8jt8jza6CfvJhI46a3y
        EQIDAQAB
        -----END PUBLIC KEY-----"
      publisherId: ="beefdead-72df-4c71-93ff-2243589006fd"
      tenantId: =Host.TenantID
      usagePermission: =DataSourceInfo('Account Gallery Usage Permissions', DataSourceInfo.ReadPermission)
`;

const powerFx = `If(
    Connection.Connected,
        ClearCollect(
            LicensesOffline,
            ShowColumns(
                Licenses,
                License,
                Identifier,
                Key
            )
        );
        SaveData(LicensesOffline, "LicensesOffline"),
        LoadData(LicensesOffline, "LicensesOffline", true)
);`

export const Component: React.FC = React.memo(() => {
    const styles = useStyles();

    return (
        <>
            <div className={styles.container}>
                <CardSection title="Prerequisites">
                    <Text>
                        Canvas Apps can easily be secured by Ianus Guard. This is done by using the Ianus Canvas PCF that is shipped in our Ianus Guard solution.
                        Be sure to have imported our Ianus Guard solution, preferably via <Link href="https://marketplace.microsoft.com/en-us/product/saas/ianuasoftware.ianus_guard?tab=overview">Microsoft AppSource</Link> or alternatively from <Link href="https://github.com/Ianua-Software/Ianus-Clients/blob/main/solutions/IanusGuard_managed.zip" target="_blank">
                            GitHub
                        </Link> into your organization before starting.
                    </Text>
                </CardSection>

                <CardSection title="Important!">
                    <Text>
                        Ianus Guard can only securely protect your component when importing it managed into your customer's environment.
                        You also have to be extra careful to set the component protected by Ianus Guard, such as your Canvas App to not allow customizations inside the managed properties.
                        Otherwise customers could edit your app and just throw out your Ianus Guard validation.
                        <br />
                        <br />
                        Here is how to set this managed property:
                        <br />
                        In your solution, click on the three dots next to its name, selected "Advanced" and then "Managed properties":
                        <br />
                        <br />
                        <Image
                            className={styles.centered}
                            alt="Open managed properties"
                            width="75%"
                            src="/assets/knowledgearticle/open-managed-properties.png"
                        />
                        <br />
                        <br />
                        In the sidepane that opens, make sure to switch the toggle off, so that no customization is possible in target environments:
                        <Image
                            className={styles.centered}
                            alt="Disable allow customizations"
                            width="75%"
                            src="/assets/knowledgearticle/allow-customizations-disabled.png"
                        />
                        <br />
                        <br />
                        Beware, this setting only applies when your component protected this way is imported in the customer's environment as managed.
                        If this is the case, your component is successfully secured and the check can not be removed by the customer.
                    </Text>

                </CardSection>

                <CardSection title="Integrate Ianus Guard">
                    <Text>
                        For validating licenses inside canvas apps, you need to first import the Code Component for "Ianus Canvas" into your app:
                        <br />
                        <br />
                        Afterwards you have to add IanusCanvas to your app and configure it appropriately:
                        <Image
                            className={styles.centered}
                            alt="Add Ianus Canvas"
                            width="75%"
                            src="/assets/knowledgearticle/ianus-canvas.png"
                        />
                        <br />
                        <br />
                        We suggest you to select the advanced mode like in above picture for filling in the parameters, as for the tenantId we will use a Power Fx formula and for the license dataset we will pass a collection variable.
                        <br />
                        Help for setting the correct parameters:
                        <ul className={styles.ul}>
                            <li><b>License Dataset(Items)</b>: Always pass the ian_license dataset without any modifications</li>
                            <li><b>Offline License Dataset (Items)</b>: Leave empty if no offline support needed. For functioning offline you should set a collection that you created in "OnStart". You can find a snippet after this listing. If you used our snippet, you should enter 'LicensesOffline' here (without quotes).</li>
                            <li><b>productId</b>: ExternalId found on your product form, surround with double quotes if using the advanced editor</li>
                            <li><b>publicKeys</b>: Found on your product form as "Public Key" inside "Product Versions", surround with double quotes if using the advanced editor</li>
                            <li><b>publisherId</b>: ExternalId found on your publisher form, surround with double quotes if using the advanced editor</li>
                            <li><b>tenantId</b>: Set the Power Fx formula 'Host.TenantID'</li>
                            <li><b>usagePermission</b>: For environment-based licensing, you can leave this empty or true. When using user-based licensing, whether the user may use your component is derived by the user being allowed to read an entity defined by you. You can find a snippet in the full yaml example at the bottom of this page.</li>
                        </ul>
                        As a rule of thumb, all information that you need to enter has a "Copy Button" next to it in Ianus Guard portal.
                        <br />
                        <strong>Important:</strong> If you choose to not use the offline license dataset, make sure to use the ian_identifier and ian_key columns somewhere in your app, as Canvas Apps try to include only the needed columns when fetching data.
                        If these columns are missing, Ianus Guard can not successfully find existing licenses, even if they exist. Also don't pass a view as parameter to license dataset or offline license dataset, as these are not updateable for us.
                        <br />
                        <br />
                        The following Power Fx function can be added for making licenses available offline. Select the "App" item in the TreeView on the left side and click on "Advanced".
                        Paste it into the OnStart input to save all available licenses offline on start of your app. If set up as described, license validation will work fully offline.
                        <br />
                        <CodeBlock code={powerFx} language="vbscript" />
                        <br />
                        <br />
                        When validating licenses, the result is available as "IsValid" property on your Ianus Guard Canvas control.
                        If the response "IsValid" property does not equal true, there is no valid license and you should act accordingly.
                        What we like to do is to add Ianus Dataset as only property on the first page and add an onChange element that navigates to your actual first content page on success.
                        A full code snippet for Ianus Dataset inside a Canvas App can then look like this:
                        <br/>
                        <br/>
                        <CodeBlock code={codeView} language="yaml" />
                    </Text>
                </CardSection>
            </div>
        </>
    );    
});